import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  LinearProgress,
  Typography,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PetsIcon from "@mui/icons-material/Pets";
import { motion, AnimatePresence } from "framer-motion";

import type { Dog, DogFormData } from "../../types";

// Step components (kept as-is)
import { BasicsStep } from "./BasicsStep";
import { PhysicalStep } from "./PhysicalStep";
import { DietaryStep } from "./DietaryStep";
import { QUIZ } from "@/theme/quizTokens";

// If you already have this shared component like in the quiz, use it.
// Otherwise, keep this modal wrapper as-is and just style it similarly.
// import { QuestionCard } from "../shared/QuestionCard";

interface Props {
  open: boolean;
  dog: Dog | null;
  onClose: () => void;
  onSave: (formData: DogFormData) => Promise<void> | void;
}

// Quiz-like palette (matches your BreedQuestion vibe)
const COLORS = {
  bg: "#FFF7F3",
  panel: QUIZ.color.white,
  ink: QUIZ.color.brown,
  inkSoft: "rgba(74,48,38,0.75)",
  border: "rgba(74,48,38,0.12)",
  borderStrong: "rgba(74,48,38,0.22)",
  accent: QUIZ.color.brown,
  accentSoft: QUIZ.color.cream2,
  primary: QUIZ.color.brown,
  primaryHover: QUIZ.color.ink,
  disabledBg: "#D0D8E0",
  disabledInk: "#8A9AAA",
};

const STEPS = [
  { key: "basics", label: "Basics", emoji: "🐕" },
  { key: "physical", label: "Physical", emoji: "💪" },
  { key: "dietary", label: "Health", emoji: "🥗" },
] as const;

// Pick an illustration like quiz questions (swap URL as needed)
const ILLUSTRATION =
  "https://cdn.shopify.com/s/files/1/0946/0622/9844/files/breeds.png?v=1767373868";

export function EnhancedDogEditModal({ open, dog, onClose, onSave }: Props) {
  const [activeStep, setActiveStep] = useState(0);
  const [form, setForm] = useState<Partial<DogFormData>>({});
  const [saving, setSaving] = useState(false);
  const [stepErrors, setStepErrors] = useState<Record<string, string>>({});
  const [shake, setShake] = useState(false);

  const validateStep = useCallback(
    (step: number): Record<string, string> => {
      const errors: Record<string, string> = {};
      if (step === 0) {
        if (!form.name || !String(form.name).trim()) errors.name = "Name is required";
        if (!form.breed || !String(form.breed).trim()) errors.breed = "Breed is required";
      } else if (step === 1) {
        if (!form.weightKg || Number(form.weightKg) <= 0) errors.weightKg = "Weight is required";
        if (!form.bodyCondition) errors.bodyCondition = "Please select a body condition";
        if (!form.activityLevel) errors.activityLevel = "Please select an activity level";
      }
      return errors;
    },
    [form]
  );

  // Initialize form when dog changes
  useEffect(() => {
    if (!dog || !open) return;

    setForm({
      name: dog.name || "",
      breed: dog.breed || "",
      sex: dog.sex,
      reproduction: dog.reproduction,
      ageYears: dog.ageYears ?? 1,
      ageMonths: dog.ageMonths ?? 0,
      weightKg: dog.weightKg ?? 10,
      bodyCondition: dog.bodyCondition,
      activityLevel: dog.activityLevel,
      allergies: dog.allergies ?? [],
      healthIssues: dog.healthIssues ?? [],
      treatFrequency: dog.treatFrequency,
    });

    setActiveStep(0);
  }, [dog, open]);

  // Allow free navigation; validate only on save
  const canSave = useMemo(() => {
    const nameOk = Boolean(form.name && String(form.name).trim().length > 0);
    const breedOk = Boolean(form.breed && String(form.breed).trim().length > 0);
    const weightOk = Boolean(form.weightKg && Number(form.weightKg) > 0);
    return nameOk && breedOk && weightOk;
  }, [form]);

  const isLastStep = activeStep === STEPS.length - 1;

  const updateForm = (updates: Partial<DogFormData>) => {
    setForm((prev) => ({ ...prev, ...updates }));
  };

  const handleClose = () => {
    setActiveStep(0);
    setForm({});
    onClose();
  };

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onSave(form as DogFormData);
      handleClose();
    } catch (error) {
      console.error("Failed to save dog:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    const errors = validateStep(activeStep);
    if (Object.keys(errors).length > 0) {
      setStepErrors(errors);
      setShake(true);
      setTimeout(() => setShake(false), 500);
      return;
    }
    setStepErrors({});
    if (isLastStep) handleSave();
    else setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    setStepErrors({});
    setActiveStep((prev) => Math.max(prev - 1, 0));
  };

  const stepTitle = useMemo(() => {
    const name = dog?.name || "your dog";
    switch (activeStep) {
      case 0:
        return `Basics about ${name}`;
      case 1:
        return `Physical details`;
      case 2:
        return `Health & diet`;
      default:
        return `Edit ${name}`;
    }
  }, [activeStep, dog?.name]);

  const stepSubtitle = useMemo(() => {
    switch (activeStep) {
      case 0:
        return "Name and breed help us personalize the plan";
      case 1:
        return "Weight, activity and condition affect calories";
      case 2:
        return "Allergies and health info keep meals safe";
      default:
        return "Update your dog’s profile";
    }
  }, [activeStep]);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <BasicsStep form={form} onChange={updateForm} errors={stepErrors} />;
      case 1:
        return <PhysicalStep form={form} onChange={updateForm} errors={stepErrors} />;
      case 2:
        return <DietaryStep form={form} onChange={updateForm} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="dog-edit-title"
      disableEnforceFocus
      sx={{
        zIndex: 1400,
        "& .MuiDialog-container": {
          alignItems: "center",
          justifyContent: "center",
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: "18px" },
          bgcolor: COLORS.panel,
          maxHeight: { xs: "100%", sm: "92vh" },
          height: { xs: "100%", sm: "auto" },
          m: { xs: 0, sm: 2.5 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(0,0,0,0.22)",
        },
      }}
    >
      {/* Quiz-style header (illustration + title) */}
      <Box
        sx={{
          position: "relative",
          flexShrink: 0,
          borderBottom: `1px solid ${COLORS.border}`,
          background: COLORS.bg,
        }}
      >
        {/* Close */}
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            bgcolor: "rgba(255,255,255,0.9)",
            border: `1px solid ${COLORS.border}`,
            "&:hover": { bgcolor: QUIZ.color.warmWhite },
          }}
        >
          <CloseIcon sx={{ color: COLORS.accent }} />
        </IconButton>

        {/* Title block */}
        <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: "12px",
                bgcolor: COLORS.accentSoft,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `1px solid ${COLORS.border}`,
              }}
            >
              <PetsIcon sx={{ color: COLORS.accent }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                id="dog-edit-title"
                sx={{
                  color: COLORS.ink,
                  fontWeight: 900,
                  fontSize: { xs: 18, sm: 20, md: 22 },
                  fontFamily: QUIZ.font.display,
                  lineHeight: 1.15,
                }}
              >
                Edit {dog?.name ? `${dog.name}’s` : "dog"} profile
              </Typography>
              <Typography
                sx={{
                  mt: 0.5,
                  color: COLORS.inkSoft,
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                Keep their details accurate for the best plan
              </Typography>
            </Box>
          </Box>

          {/* Step navigation chips (quiz-like) */}
          <Box sx={{ display: "flex", gap: 1, mt: 2, mb: 1.5 }}>
            {STEPS.map((step, idx) => {
              const isActive = idx === activeStep;
              const isCompleted = idx < activeStep;

              return (
                <Box
                  key={step.key}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setStepErrors({}); setActiveStep(idx); }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") { setStepErrors({}); setActiveStep(idx); }
                  }}
                  sx={{
                    flex: 1,
                    py: 1,
                    px: 1.25,
                    borderRadius: "12px",
                    cursor: "pointer",
                    userSelect: "none",
                    border: `1px solid ${isActive ? "rgba(74, 48, 38,0.35)" : COLORS.border}`,
                    bgcolor: isActive
                      ? "rgba(74, 48, 38,0.16)"
                      : isCompleted
                        ? "rgba(46,125,50,0.10)"
                        : QUIZ.color.white,
                    transition: "all 0.18s ease",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.75,
                    "&:hover": {
                      transform: "translateY(-1px)",
                      borderColor: isActive ? "rgba(74, 48, 38,0.45)" : COLORS.borderStrong,
                      bgcolor: isActive ? "rgba(74, 48, 38,0.18)" : "rgba(255,225,221,0.35)",
                    },
                  }}
                >
                  {isCompleted ? (
                    <CheckCircleIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
                  ) : (
                    <Typography sx={{ fontSize: 14 }}>{step.emoji}</Typography>
                  )}

                  <Typography
                    sx={{
                      fontSize: { xs: 11, sm: 13 },
                      fontWeight: 900,
                      color: isActive ? COLORS.accent : isCompleted ? "#2E7D32" : COLORS.inkSoft,
                    }}
                  >
                    {step.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={((activeStep + 1) / STEPS.length) * 100}
            sx={{
              height: 4,
              borderRadius: 2,
              bgcolor: "rgba(74,48,38,0.08)",
              "& .MuiLinearProgress-bar": {
                bgcolor: COLORS.primary,
                borderRadius: 2,
                transition: "transform 0.3s ease",
              },
            }}
          />
        </Box>
      </Box>

      {/* Content (quiz-like: title/subtitle + animated step) */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          bgcolor: QUIZ.color.warmWhite,
        }}
      >
        <Box
          sx={{
            maxWidth: 600,
            mx: "auto",
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 2.5 },
            pb: { xs: 4, sm: 2 },
          }}
        >
          <Typography
            sx={{
              color: COLORS.ink,
              fontWeight: 900,
              fontSize: { xs: 18, sm: 20 },
              fontFamily: QUIZ.font.display,
              textAlign: "center",
              mb: 0.5,
            }}
          >
            {stepTitle}
          </Typography>
          <Typography
            sx={{
              color: COLORS.inkSoft,
              fontSize: 14,
              fontWeight: 600,
              mb: 2,
              textAlign: "center",
            }}
          >
            {stepSubtitle}
          </Typography>

          <AnimatePresence mode="wait">
            <motion.div
              key={STEPS[activeStep]?.key ?? String(activeStep)}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.2 }}
            >
              <Box
                sx={{
                  backgroundColor: COLORS.bg,
                  border: `1px solid ${Object.keys(stepErrors).length > 0 ? "rgba(211,47,47,0.4)" : COLORS.border}`,
                  borderRadius: "16px",
                  p: { xs: 2, sm: 2.5 },
                  animation: shake ? "shake 0.4s ease-in-out" : "none",
                  "@keyframes shake": {
                    "0%, 100%": { transform: "translateX(0)" },
                    "20%": { transform: "translateX(-6px)" },
                    "40%": { transform: "translateX(6px)" },
                    "60%": { transform: "translateX(-4px)" },
                    "80%": { transform: "translateX(4px)" },
                  },
                }}
              >
                {renderStepContent()}
              </Box>
            </motion.div>
          </AnimatePresence>
        </Box>
      </Box>

      {/* Sticky Footer (quiz-like buttons) */}
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: 2,
          bgcolor: QUIZ.color.warmWhite,
          borderTop: `1px solid ${COLORS.border}`,
          flexShrink: 0,
          display: "flex",
          gap: 1.5,
        }}
      >
        {activeStep > 0 ? (
          <Button
            onClick={handleBack}
            disabled={saving}
            sx={{
              flex: 1,
              py: 1.45,
              minHeight: { xs: 48, sm: "auto" },
              borderRadius: "12px",
              border: `2px solid ${COLORS.accent}`,
              color: COLORS.accent,
              fontWeight: 900,
              fontSize: 14,
              textTransform: "none",
              bgcolor: QUIZ.color.warmWhite,
              "&:hover": { bgcolor: "rgba(255,225,221,0.35)" },
              "&:disabled": {
                opacity: 0.6,
                borderColor: COLORS.disabledBg,
                color: COLORS.disabledInk,
              },
            }}
          >
            Back
          </Button>
        ) : (
          <Button
            onClick={handleClose}
            disabled={saving}
            sx={{
              flex: 1,
              py: 1.45,
              minHeight: { xs: 48, sm: "auto" },
              borderRadius: "12px",
              border: `2px solid ${COLORS.borderStrong}`,
              color: COLORS.inkSoft,
              fontWeight: 900,
              fontSize: 14,
              textTransform: "none",
              bgcolor: QUIZ.color.warmWhite,
              "&:hover": { bgcolor: "rgba(74,48,38,0.05)" },
              "&:disabled": { opacity: 0.6 },
            }}
          >
            Cancel
          </Button>
        )}

        <Button
          onClick={handleNext}
          disabled={(isLastStep && !canSave) || saving}
          sx={{
            flex: 1,
            py: 1.45,
            minHeight: { xs: 48, sm: "auto" },
            borderRadius: "12px",
            bgcolor: COLORS.primary,
            color: QUIZ.color.white,
            fontWeight: 900,
            fontSize: 14,
            textTransform: "none",
            boxShadow: "0 10px 24px rgba(74, 48, 38,0.28)",
            "&:hover": { bgcolor: COLORS.primaryHover },
            "&:disabled": {
              bgcolor: COLORS.disabledBg,
              color: COLORS.disabledInk,
              boxShadow: "none",
            },
          }}
        >
          {saving ? "Saving..." : isLastStep ? "Save Changes" : "Continue"}
        </Button>
      </Box>
    </Dialog>
  );
}